import { resolveBadgeFromLifecycleCodes } from '@/utils/product/lifecycle';
import { normalizeBrandName } from './normalize-brand-name';
import type { BadgeVariant } from '@/components/atoms/badge/types';
import type { AlgoliaProduct } from '@/lib/algolia';
import type { Currency } from '@/types/currency';
import type { Product } from '@/types/entity/product';
import type { ProductListingProductEntry } from '@/components/pages/product-listing-v2/types';

// EWM-Algolia v1 — Algolia record → ProductListingProductEntry.
//
// Maps an `sd*` Algolia record into the entity shape `ProductListingV2`
// consumes, bypassing the BFF `Product` mapper. v1 ships the minimum
// surface the v2 PLP tile reads (name, sku, image, price, brand, stock,
// url). Tier pricing / per-channel stock / product-selection scoping
// are deferred to v2 (see `docs/superpowers/plans/...`).

const MEDIA_IMAGE_BASE = 'https://media.jaycar.com.au/product/images/';

// `sd*` records carry either fully-qualified URLs (retail-shape) or
// bare filenames (B2B indexing pipeline today). Patch on the FE for v1.
const resolveImageUrl = (raw?: string): string | undefined => {
  if (!raw) return undefined;
  return /^https?:\/\//.test(raw) ? raw : `${MEDIA_IMAGE_BASE}${raw}`;
};

// Live `sd*` records diverge from the typed schema in ways the indexing
// pipeline hasn't normalised yet (verified 2026-05-12). The helpers
// below coerce both legal and observed-but-invalid shapes so the mapper
// renders something sensible instead of crashing.
const asArray = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

const firstImage = (hit: AlgoliaProduct): { url?: string; altText?: string } | undefined => {
  const entry = asArray<unknown>(hit.images).find(
    (e): e is { productListingImage?: { url?: string; altText?: string } } =>
      !!e && typeof e === 'object' && !Array.isArray(e),
  );
  return entry?.productListingImage;
};

/**
 * The product's lifecycle code from an Algolia hit.
 *
 * EWM-643 — the TOP-LEVEL `lifeCycleAttribute` is the live field and is read
 * first. The nested `productLifeCycleTags` object is RETIRED: it is `null` on
 * every production record, so reading it alone left every prod PLP/search card
 * with no lifecycle badge at all. The nested read stays as a fallback because
 * the dev index still carries both, and on dev-shaped records it appears as an
 * OBJECT (`{lifeCycleAttribute, color, …}`) rather than the `Array<{…}>` the
 * type declares — hence both shapes below. Drop the fallback once every index
 * generation has been reindexed.
 */
export const lifecycleCodeFromHit = (hit: AlgoliaProduct): string | undefined => {
  if (typeof hit.lifeCycleAttribute === 'string' && hit.lifeCycleAttribute.length > 0) {
    return hit.lifeCycleAttribute;
  }
  const raw = hit.productLifeCycleTags as unknown;
  if (Array.isArray(raw)) {
    return raw.find((e) => e && typeof e === 'object' && 'lifeCycleAttribute' in e)?.lifeCycleAttribute;
  }
  if (raw && typeof raw === 'object' && 'lifeCycleAttribute' in raw) {
    const code = (raw as { lifeCycleAttribute?: unknown }).lifeCycleAttribute;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
};

const lifecycleCode = lifecycleCodeFromHit;

// EWM-241 (OQ-13/14/16, Luke 2026-06-13) — the search-card marketing badge
// is derived from the `productLifeCycle` code per the Life Cycle Attribute
// list, NOT the price `discountedPrice < price` check (OQ-16). Precedence and
// the code→variant mapping live in the shared `resolveBadgeFromLifecycleCodes`
// (EWM-657) so the PDP resolves the identical badge from CT lifecycle codes.
const resolveBadge = (code?: string): BadgeVariant | undefined => resolveBadgeFromLifecycleCodes(code ? [code] : []);

// `brand` is a STRING on live sd* (e.g. `"NEXTECH"`), not the `string[]`
// the type declares. Reading `hit.brand?.[0]` returned the first character
// before this fix. On sd* indices the string is sometimes the JSON envelope
// `{"brandInfo":{"name":...}}` (EWM-327) — `normalizeBrandName` handles the
// plain-string, envelope and legacy `string[]` shapes uniformly.
const brandLabel = (hit: AlgoliaProduct): string | undefined => normalizeBrandName(hit.brand);

// sd* records carry pricing as a TOP-LEVEL `rrp` number (in dollars) and
// have no `priceGroup` at all. The retail (`jr*`/`rt*`) shape has
// `priceGroup[0].activePrice/rrPrice` in cents. Honour both so the same
// mapper works against B2C and B2B indices.
interface PricingResult {
  price: number;
  rrpPrice?: number;
  currency: Currency;
}
const resolvePricing = (hit: AlgoliaProduct): PricingResult => {
  const priceGroup = asArray<{
    activePrice?: { centAmount?: number; currencyCode?: string };
    rrPrice?: { centAmount?: number };
  }>(hit.priceGroup);

  const active = priceGroup[0]?.activePrice;
  const rrpFromGroup = priceGroup[0]?.rrPrice;
  if (typeof active?.centAmount === 'number') {
    return {
      price: active.centAmount / 100,
      rrpPrice: typeof rrpFromGroup?.centAmount === 'number' ? rrpFromGroup.centAmount / 100 : undefined,
      currency: (active.currencyCode ?? 'AUD') as Currency,
    };
  }

  // B2B sd* fallback: top-level `rrp` is dollars (not cents).
  const topRrp = (hit as unknown as { rrp?: unknown }).rrp;
  const price = typeof topRrp === 'number' ? topRrp : 0;
  return { price, currency: 'AUD' as Currency };
};

export const mapAlgoliaProduct = (hit: AlgoliaProduct): ProductListingProductEntry => {
  const { price, rrpPrice, currency } = resolvePricing(hit);
  const image = firstImage(hit);
  const imageUrl = resolveImageUrl(image?.url);

  // Treat `activePrice < rrPrice` as the discount signal — only meaningful
  // when both numbers are present (retail-shape records). sd* records
  // today only ship a single `rrp`, so `hasDiscount` is always false.
  const hasDiscount = typeof rrpPrice === 'number' && rrpPrice > price && price > 0;

  const brand = brandLabel(hit);
  const code = lifecycleCode(hit);
  const isDiscontinued = code === 'Z' || code === 'Zed';
  // EWM-360 — `GO` = removed from sale: tile stays visible but Add to Cart
  // is always disabled (no back-order exception).
  const isRemovedFromSale = code === 'GO';
  // EWM-545 — `N` (new) / `P` (pending) lifecycle products are new arrivals
  // awaiting their first stock; when out of stock they read "Stock due soon".
  const isStockDueSoonEligible = code === 'N' || code === 'P';

  const item: Product = {
    id: hit.objectID,
    key: hit.key,
    sku: hit.sku,
    name: hit.name ?? '',
    description: hit.description,
    images: imageUrl ? [imageUrl] : [],
    price: hasDiscount ? rrpPrice! : price,
    ...(hasDiscount ? { discountedPrice: price } : {}),
    currency,
    // EWM-966 — displayed stock is owned exclusively by the CT enrichment
    // layer (`applyCtSnapshot` in `product-tile-with-ct`), which overrides
    // this when the CT snapshot resolves and forces "Out of stock" while it
    // is pending or missing. Fail closed: never emit a displayable in-stock
    // from Algolia data.
    //
    // EWM-686 (2026-09-17) — the REASON, corrected. This comment used to say
    // Algolia's `isAvailable` is a stock flag refreshed "on a slower cadence
    // than CT". It is not stale stock; it is not stock at all. Measured by
    // direct REST query on all four live indices: `isAvailable` is `false` if
    // and only if `lifeCycleAttribute` is "GO", zero exceptions,
    // `exhaustiveNbHits: true` — a lifecycle alias for "not removed from
    // sale". As at that measurement the Algolia record carried no
    // stock/inventory attribute of any kind, so there was nothing here to fall
    // back to and no cadence to wait for. EWM-966's fail-closed `false` is
    // still exactly right, for this stronger reason. See
    // `algolia.record.in-stock-attr-discriminates` in
    // `docs/DATA-ASSUMPTIONS.md`.
    //
    // UPDATE 2026-09-18 — a stock attribute now EXISTS and this line does not
    // change. EWM-1039 delivered `inStock` (true = in stock), facetable on all
    // six indices and carried by the dev/uat AU records; it agrees with
    // commercetools on the SKUs QA flagged, which `isAvailable` never did. It
    // is still NOT a fallback for this mapper: coverage is 0 on dev NZ, uat NZ,
    // prod AU and prod NZ, and 8,800 of 9,411 visible records on dev AU
    // (measured 10:58 (+08:00)), so reading it here would emit "in stock" from
    // an attribute most of the catalogue does not carry. CT remains the only
    // source of the displayed badge (EWM-966), and `AlgoliaProduct` deliberately
    // does not declare `inStock` — see the MIND THE NAME note in
    // `lib/tastics/product-list/components/product-list-view-model/index.tsx`.
    inStock: false,
    // PDP URL pattern is `/{slug}/p/{sku}` — see `backend/commerce-commercetools/
    // utils/routers/ProductRouter.ts`. Bare `/p/<slug>` is NOT a Frontastic
    // route, so generating it produced 404s for every search-result click.
    url: hit.slug && hit.sku ? `/${hit.slug}/p/${hit.sku}` : undefined,
    ...(brand ? { brand } : {}),
    ...(isDiscontinued ? { isDiscontinued: true } : {}),
    ...(isRemovedFromSale ? { isRemovedFromSale: true } : {}),
    ...(isStockDueSoonEligible ? { isStockDueSoonEligible: true } : {}),
  };

  return {
    item,
    brand,
    badge: resolveBadge(code),
  };
};
